'use strict';

// ---------- Detección de tonos DTMF (Goertzel) ----------
//
// Port 1:1 de src/dtmf-service.js del proyecto Electron. La función
// detectDigitInBlock() es matemática pura (sin child_process, sin Node): se
// copia tal cual, con los mismos umbrales, calibrados sobre 55s de llamada
// real (ver ARQUITECTURA.md, sección 8 y 9).

export const SAMPLE_RATE = 8000;
export const BLOCK_SIZE = 205; // ~25.6 ms a 8 kHz — tamaño clásico para Goertzel/DTMF

const ROW_FREQS = [697, 770, 852, 941];
const COL_FREQS = [1209, 1336, 1477, 1633];
const DIGIT_MAP = [
  ['1', '2', '3', 'A'],
  ['4', '5', '6', 'B'],
  ['7', '8', '9', 'C'],
  ['*', '0', '#', 'D'],
];

const SILENCE_ENERGY = 205 * 40 * 40;
const ENERGY_FRACTION = 0.12;
const PEAK_RATIO = 3;
const TWIST_RATIO = 6;
export const CONFIRM_BLOCKS = 2;
const ARMONICO_RATIO = 4;

function goertzelPower(samples, freq) {
  const N = samples.length;
  const k = Math.round((N * freq) / SAMPLE_RATE);
  const omega = (2 * Math.PI * k) / N;
  const coeff = 2 * Math.cos(omega);
  let s1 = 0;
  let s2 = 0;
  for (let i = 0; i < N; i++) {
    const s0 = samples[i] + coeff * s1 - s2;
    s2 = s1;
    s1 = s0;
  }
  return s1 * s1 + s2 * s2 - coeff * s1 * s2;
}

function maxIndex(arr) {
  let idx = 0;
  for (let i = 1; i < arr.length; i++) if (arr[i] > arr[idx]) idx = i;
  return idx;
}

export function detectDigitInBlock(samples) {
  let energy = 0;
  for (let i = 0; i < samples.length; i++) energy += samples[i] * samples[i];
  if (energy < SILENCE_ENERGY) return null;

  const rowPowers = ROW_FREQS.map((f) => goertzelPower(samples, f));
  const colPowers = COL_FREQS.map((f) => goertzelPower(samples, f));

  const rowIdx = maxIndex(rowPowers);
  const colIdx = maxIndex(colPowers);
  const rowPower = rowPowers[rowIdx];
  const colPower = colPowers[colIdx];

  if (rowPower / energy < ENERGY_FRACTION || colPower / energy < ENERGY_FRACTION) return null;

  const all = [...rowPowers, ...colPowers].sort((a, b) => b - a);
  const thirdStrongest = all[2];
  if (thirdStrongest > 0 && Math.min(rowPower, colPower) < thirdStrongest * PEAK_RATIO) return null;

  const twist = rowPower > colPower ? rowPower / colPower : colPower / rowPower;
  if (twist > TWIST_RATIO) return null;

  const armFila = goertzelPower(samples, ROW_FREQS[rowIdx] * 2);
  const armCol = goertzelPower(samples, COL_FREQS[colIdx] * 2);
  if (armFila > rowPower / ARMONICO_RATIO || armCol > colPower / ARMONICO_RATIO) return null;

  return DIGIT_MAP[rowIdx][colIdx];
}
